import * as angular from 'angular';
import * as _ from "lodash";
import {apsViewModels} from "../../shared/dto/APS.ViewModels";
import {apsEnums} from "../../shared/dto/aps.enums";
import {apsEvents} from "../../shared/dto/aps.events";
import {StateParams} from "../../common";
import {Strings} from "../../shared/config/constant.module";
import DateUtilityService from "../../services/utility/dateutility.service";
import FillOutCheckList, {openFillOutCheckList} from "./fillOutCheckList.controller";
import CheckListInstancesServiceName, {CheckListInstancesService} from "../../services/checkLists/checkListInstances.service";
import AuthDataServiceName, {AuthDataService} from "../../services/auth/authData.service";
import ShopServiceName, {ShopService} from "../../services/shops/shops.service";
import CheckListInstancesHubName, {CheckListInstancesHubCreator, CheckListInstancesHub} from "../../services/checkLists/checkListInstances.hub";

let requires = [FillOutCheckList, CheckListInstancesServiceName, AuthDataServiceName, ShopServiceName,
  CheckListInstancesHubName];

interface RecentCheckListsController {
  list: apsViewModels.CheckListInstanceListItem[];
  loading: boolean;
  formatTime(time): string;
  open(checkListInstance: apsViewModels.CheckListInstanceListItem): void;
  getStatusName(status: apsEnums.CheckListStatus): string;
  viewAllChanged(viewAll: boolean): void;
}

class RecentCheckListsCtrl implements RecentCheckListsController {
  list: apsViewModels.CheckListInstanceListItem[];
  loading: boolean;
  private viewAll: boolean;
  private plantId: string;
  private shopId: string;
  private shop: apsViewModels.ShopConfiguration;
  private timeZoneId: string;
  private $modal: mm.foundation.modal;
  private checkListInstancesService: CheckListInstancesService;
  private shopPromise: angular.IPromise<any>;
  private $q: angular.IQService;
  private authDataService: AuthDataService;
  private hub: CheckListInstancesHub;
  private $scope: angular.IScope;
  private $timeout: angular.ITimeoutService;

  constructor(
    checkListInstancesService: CheckListInstancesService,
    $modal: mm.foundation.modal,
    $q: angular.IQService,
    authDataService: AuthDataService,
    shopService: ShopService,
    checkListInstancesHubCreator: CheckListInstancesHubCreator,
    $scope: angular.IScope,
    $timeout: angular.ITimeoutService,
    $stateParams: StateParams) {
    this.plantId = $stateParams.plantId;
    this.shopId = $stateParams.shopId;
    this.timeZoneId = Strings.DEFAULT_TIMEZONE;
    this.$modal = $modal;
    this.checkListInstancesService = checkListInstancesService;
    this.$q = $q;
    this.$scope = $scope;
    this.$timeout = $timeout;
    this.loading = true;
    this.viewAll = false;

    this.authDataService = authDataService;

    this.shopPromise = shopService
      .getShopConfiguration(this.shopId)
      .then((result) => {
        this.shop = result;
        this.timeZoneId = result.timeZoneId;
      }
    );

    this.hub = checkListInstancesHubCreator.createCheckListInstancesHub(
      this.plantId, this.shopId,
      (updated) => this.handleUpdated(updated),
      (instance) => this.handleInstance(instance));

    this.$scope.$on('$destroy', () => {
      if (this.hub) {
        this.hub.uninitialize();
      }
    });
  }

  private handleUpdated(updated: apsEvents.CheckListInstancesUpdated) {
    if (!this.list) {
      return;
    }

    if (updated.instances.length > 0) {
      _.forEach(updated.instances, i => this.handleUpdated(i));
    }

    var existing = _.find(this.list, {id: updated.id});
    if (!existing) {
      return;
    }

    this.$timeout(() => {
      if (updated.action === apsEnums.EventAction.Delete
        || updated.status < apsEnums.CheckListStatus.Complete) {
        _.remove(this.list, existing);
      } else {
        existing.status = updated.status;
        existing.result = updated.result;
      }
    });
  }

  private handleNewInstance(instance: apsViewModels.CheckListInstanceItemEvent): boolean {
    if (instance.status < apsEnums.CheckListStatus.Complete || instance.checkListIsGroup) {
      return false;
    }

    var currentRole = this.authDataService.getSelectedRoleLocation();
    var roleId = currentRole.role.roleId;
    if (instance.roleId !== roleId
      && !_.some(instance.allowableRoles, r => r === roleId)) {
      return false;
    }

    this.list.splice(0, 0, instance);
    return true;
  }

  private handleInstance(instance: apsViewModels.CheckListInstanceItemEvent) {
    if (!this.list) {
      return;
    }

    if (instance.instances.length > 0) {
      _.forEach(instance.instances, i => this.handleInstance(i));
    }

    this.$timeout(() => {
      if (instance.action === apsEnums.EventAction.Add) {
        if (!this.handleNewInstance(instance)) {
          return;
        }
      } else {
        var existing = _.find(this.list, {id: instance.id});
        if (!existing) {
          if (!this.handleNewInstance(instance)) {
            return;
          }
        } else if (instance.status < apsEnums.CheckListStatus.Complete) {
          _.remove(this.list, existing);
        } else {
          existing.status = instance.status;
          existing.result = instance.result;
        }
      }
    });
  }

  private getData() {
    var currentRole = this.authDataService.getSelectedRoleLocation();
    var roleId = currentRole.role.roleId;
    return this.checkListInstancesService
      .getRecentInstances(this.plantId, this.shopId, roleId, this.viewAll)
      .then((data) => {
        this.list = _.filter(data, (instance) => this.checkListInstancesService.isAuthorizedForCurrentPlant(instance));
      });
  }

  viewAllChanged(viewAll: boolean): void {
    this.viewAll = viewAll;
    this.loading = true;
    this.$q.all([this.shopPromise, this.getData()]).then(() => this.loading = false);
  }

  formatTime(time) {
    return DateUtilityService.formatTime(time, this.timeZoneId);
  }

  getStatusName(status: apsEnums.CheckListStatus) {
    return _.startCase(apsEnums.CheckListStatus[status]);
  }

  open(checkListInstance: apsViewModels.CheckListInstanceListItem) {
    var result = openFillOutCheckList({
      checkListId: checkListInstance.checkListId,
      instanceId: checkListInstance.id,
      plantId: checkListInstance.plantId,
      tankId: checkListInstance.tankId,
      shopId: checkListInstance.shopId,
      plantLocationId: checkListInstance.plantLocationId,
      readOnly: false,
      scheduledOn: checkListInstance.scheduledOn
    }, this.$modal);

    result.then((cmd) => {
      var data = {
        status: checkListInstance.status,
        result: apsEnums.CheckListResult.Incomplete
      };
      this.checkListInstancesService.updateStatusAndResult(data, cmd);
      checkListInstance.status = data.status;
    });
  }
}

export default angular.module('aps.controller.recentCheckLists', requires)
  .controller('RecentCheckListsCtrl', RecentCheckListsCtrl)
  .name;


// WEBPACK FOOTER //
// ./src/app/controllers/checks/recentCheckLists.controller.ts